import { Link } from 'react-router-dom';
import { Shield, Mail } from 'lucide-react';
import SEOHead from '../components/SEOHead';

const PrivacyPolicyPage = () => {
    return (
        <div className="min-h-screen" data-testid="privacy-page">
            <SEOHead 
                title="Privacy Policy | Craven Canines"
                description="Read how Craven Canines collects, uses, and protects your personal information. Your privacy matters."
                canonical="/privacy"
            />
            {/* Hero Section */}
            <section className="relative py-16 md:py-20 bg-primary/5">
                <div className="container-custom">
                    <div className="max-w-3xl mx-auto text-center">
                        <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-medium mb-6">
                            <Shield className="w-4 h-4" /> Your Privacy Matters
                        </div>
                        <h1 className="text-4xl md:text-5xl font-bold text-foreground mb-6 leading-tight font-serif">
                            Privacy Policy
                        </h1>
                        <p className="text-muted-foreground">
                            Last updated: February 2026
                        </p>
                    </div>
                </div>
            </section>

            {/* Content Section */}
            <section className="section-spacing">
                <div className="container-custom">
                    <div className="max-w-3xl mx-auto prose prose-lg">
                        <div className="space-y-8">
                            <div>
                                <h2 className="text-2xl font-bold text-foreground font-serif mb-4">Introduction</h2>
                                <p className="text-muted-foreground leading-relaxed">
                                    Craven Canines ("we", "our", or "us") is committed to protecting your privacy. This Privacy Policy 
                                    explains how we collect, use, and safeguard your personal information when you use our website 
                                    and services.
                                </p>
                            </div>

                            <div>
                                <h2 className="text-2xl font-bold text-foreground font-serif mb-4">Information We Collect</h2>
                                <p className="text-muted-foreground leading-relaxed mb-4">
                                    We may collect the following types of information:
                                </p>
                                <ul className="list-disc list-inside text-muted-foreground space-y-2">
                                    <li><strong>Contact Information:</strong> Name, email address, phone number when you book a session or contact us</li>
                                    <li><strong>Booking Information:</strong> Details about your dog, session preferences, and booking history</li>
                                    <li><strong>Payment Information:</strong> Payment details are processed securely through Stripe and are not stored on our servers</li>
                                    <li><strong>Website Usage:</strong> Information about how you use our website, including pages visited and time spent</li>
                                </ul>
                            </div>

                            <div>
                                <h2 className="text-2xl font-bold text-foreground font-serif mb-4">How We Use Your Information</h2>
                                <p className="text-muted-foreground leading-relaxed mb-4">
                                    We use your information to:
                                </p>
                                <ul className="list-disc list-inside text-muted-foreground space-y-2">
                                    <li>Process and manage your bookings</li>
                                    <li>Communicate with you about sessions, updates, and changes</li>
                                    <li>Send booking confirmations and reminders</li>
                                    <li>Improve our services and website</li>
                                    <li>Respond to your enquiries and provide customer support</li>
                                </ul>
                            </div>

                            <div>
                                <h2 className="text-2xl font-bold text-foreground font-serif mb-4">Data Security</h2>
                                <p className="text-muted-foreground leading-relaxed">
                                    We take appropriate security measures to protect your personal information. Payment processing 
                                    is handled securely by Stripe, a PCI-compliant payment processor. We do not store your full 
                                    payment card details on our systems.
                                </p>
                            </div>

                            <div>
                                <h2 className="text-2xl font-bold text-foreground font-serif mb-4">Data Retention</h2>
                                <p className="text-muted-foreground leading-relaxed">
                                    We retain your personal information only for as long as necessary to fulfil the purposes for 
                                    which it was collected, including to satisfy legal, accounting, or reporting requirements.
                                </p>
                            </div>

                            <div>
                                <h2 className="text-2xl font-bold text-foreground font-serif mb-4">Your Rights</h2>
                                <p className="text-muted-foreground leading-relaxed mb-4">
                                    Under data protection laws, you have rights including:
                                </p>
                                <ul className="list-disc list-inside text-muted-foreground space-y-2">
                                    <li><strong>Access:</strong> Request a copy of your personal data</li>
                                    <li><strong>Correction:</strong> Request correction of inaccurate data</li>
                                    <li><strong>Deletion:</strong> Request deletion of your personal data</li>
                                    <li><strong>Objection:</strong> Object to processing of your personal data</li>
                                </ul>
                            </div>

                            <div>
                                <h2 className="text-2xl font-bold text-foreground font-serif mb-4">Cookies</h2>
                                <p className="text-muted-foreground leading-relaxed">
                                    Our website may use cookies to enhance your browsing experience. Cookies are small text files 
                                    stored on your device. You can control cookie settings through your browser preferences.
                                </p>
                            </div>

                            <div>
                                <h2 className="text-2xl font-bold text-foreground font-serif mb-4">Third-Party Services</h2>
                                <p className="text-muted-foreground leading-relaxed">
                                    We use third-party services for payment processing (Stripe). These services have their own 
                                    privacy policies governing the use of your information.
                                </p>
                            </div>

                            <div>
                                <h2 className="text-2xl font-bold text-foreground font-serif mb-4">Changes to This Policy</h2>
                                <p className="text-muted-foreground leading-relaxed">
                                    We may update this Privacy Policy from time to time. Any changes will be posted on this page 
                                    with an updated revision date.
                                </p>
                            </div>

                            <div className="bg-secondary/50 rounded-2xl p-6">
                                <h2 className="text-2xl font-bold text-foreground font-serif mb-4">Contact Us</h2>
                                <p className="text-muted-foreground leading-relaxed mb-4">
                                    If you have any questions about this Privacy Policy or how we handle your data, please contact us:
                                </p>
                                <div className="flex items-center gap-3 text-primary">
                                    <Mail className="w-5 h-5" />
                                    <a href="mailto:infocravencanines@gmail.com" className="hover:underline">
                                        infocravencanines@gmail.com
                                    </a>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default PrivacyPolicyPage;
